/**
 * 管理パネル — /admin
 * パスワード「nyanyanya」で入れる管理者専用画面
 * 全投稿が見えて、各投稿に削除ボタンがついている
 */
import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface Post {
  id: string;
  author: string;
  message: string;
  created_at: string;
  ip_hash: string | null;
  is_deleted: boolean;
}

const YEAR_OFFSET_MS = 200 * 365.25 * 24 * 60 * 60 * 1000;

function toMatoDate(isoStr: string): string {
  const d = new Date(new Date(isoStr).getTime() + YEAR_OFFSET_MS);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${y}/${m}/${day} ${hh}:${mm}`;
}

/* ─────────────────────────────────────────
   ログイン画面
───────────────────────────────────────── */
function LoginScreen({ onLogin }: { onLogin: () => void }) {
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(false);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (pw === "nyanyanya") {
      onLogin();
    } else {
      setErr("パスワードが違います");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      setPw("");
    }
  }

  return (
    <div className="min-h-screen bg-[#0d0d1a] flex items-center justify-center">
      {/* 背景グリッド */}
      <div
        className="fixed inset-0 opacity-5"
        style={{
          backgroundImage:
            "linear-gradient(rgba(100,100,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(100,100,255,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />
      <div
        className={`relative z-10 bg-[#1a1a2e] border border-[#3a3a6a] rounded-xl p-8 w-full max-w-sm shadow-2xl transition-transform ${shake ? "animate-[shake_0.3s_ease]" : ""}`}
        style={shake ? { animation: "shake 0.3s ease" } : {}}
      >
        {/* ロゴ */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full border-2 border-[#5555aa]/60 mb-3"
            style={{ background: "rgba(20,20,60,0.8)" }}>
            <span className="text-2xl">🔐</span>
          </div>
          <h1 className="text-white font-bold text-lg tracking-widest">管理パネル</h1>
          <p className="text-[#5555aa] text-[10px] tracking-wider mt-1">
            DEMON CITY BBS ADMIN
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-[10px] text-[#7777aa] block mb-1 tracking-wider">
              管理者パスワード
            </label>
            <input
              type="password"
              value={pw}
              onChange={e => setPw(e.target.value)}
              placeholder="••••••••"
              autoFocus
              className="w-full bg-[#0d0d1a] border border-[#3a3a6a] rounded-lg px-4 py-2.5 text-white text-sm outline-none focus:border-[#7777cc] transition-colors placeholder:text-[#444]"
            />
          </div>
          {err && (
            <p className="text-red-400 text-xs bg-red-900/20 border border-red-900/40 rounded px-3 py-2">
              ⚠ {err}
            </p>
          )}
          <button
            type="submit"
            className="w-full bg-[#3333aa] hover:bg-[#4444cc] text-white text-sm py-2.5 rounded-lg transition-colors font-bold tracking-wider"
          >
            ログイン
          </button>
        </form>

        <div className="mt-4 text-center">
          <a
            href="#/mato-bbs"
            className="text-[10px] text-[#5555aa] hover:text-[#7777cc] transition-colors"
          >
            ← 掲示板に戻る
          </a>
        </div>
      </div>

      {/* shakeアニメーション */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20% { transform: translateX(-10px); }
          40% { transform: translateX(10px); }
          60% { transform: translateX(-8px); }
          80% { transform: translateX(8px); }
        }
      `}</style>
    </div>
  );
}

/* ─────────────────────────────────────────
   管理パネル本体
───────────────────────────────────────── */
function AdminPanelContent({ onLogout }: { onLogout: () => void }) {
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "active" | "deleted">("all");
  const [searchText, setSearchText] = useState("");

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("bbs_posts")
      .select("id, author, message, created_at, ip_hash, is_deleted")
      .order("created_at", { ascending: false });

    if (!error && data) {
      setPosts(data);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  async function handleDelete(postId: string) {
    setDeletingId(postId);
    try {
      const { data, error } = await supabase.rpc("admin_delete_post", {
        p_post_id: postId,
        p_password: "nyanyanya",
      });
      if (error) throw error;
      const result = data as { success: boolean; error?: string };
      if (result?.success) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_deleted: true } : p));
      }
    } catch (_e) {
      // エラーは無視
    } finally {
      setDeletingId(null);
    }
  }

  async function handleRestore(postId: string) {
    setDeletingId(postId);
    try {
      const { error } = await supabase
        .from("bbs_posts")
        .update({ is_deleted: false })
        .eq("id", postId);
      if (!error) {
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, is_deleted: false } : p));
      }
    } catch (_e) {
      // エラーは無視
    } finally {
      setDeletingId(null);
    }
  }

  const filteredPosts = posts.filter(p => {
    const matchFilter =
      filter === "all" ? true :
      filter === "active" ? !p.is_deleted :
      p.is_deleted;
    const matchSearch = searchText
      ? p.author.includes(searchText) || p.message.includes(searchText)
      : true;
    return matchFilter && matchSearch;
  });

  const activeCount = posts.filter(p => !p.is_deleted).length;
  const deletedCount = posts.filter(p => p.is_deleted).length;

  return (
    <div className="min-h-screen bg-[#0d0d1a]">
      {/* 背景グリッド */}
      <div
        className="fixed inset-0 opacity-5 pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(rgba(100,100,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(100,100,255,0.5) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* ヘッダー */}
      <div
        className="relative z-10 border-b border-[#2a2a4a]"
        style={{ background: "linear-gradient(135deg, #0a0a2e 0%, #1a1a4e 50%, #0d0d3a 100%)" }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-2xl">🛡️</span>
            <div>
              <h1 className="text-white font-bold text-base tracking-widest">
                BBS 管理パネル
              </h1>
              <p className="text-[#5555aa] text-[10px] tracking-wider">
                DEMON CITY ADMIN CONSOLE
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={fetchPosts}
              className="text-[10px] text-[#7777aa] hover:text-white bg-[#1a1a3a] hover:bg-[#2a2a5a] border border-[#3a3a6a] px-3 py-1.5 rounded transition-colors"
            >
              ↻ 更新
            </button>
            <a
              href="#/mato-bbs"
              className="text-[10px] text-[#7777aa] hover:text-white bg-[#1a1a3a] hover:bg-[#2a2a5a] border border-[#3a3a6a] px-3 py-1.5 rounded transition-colors"
            >
              掲示板へ
            </a>
            <button
              onClick={onLogout}
              className="text-[10px] text-red-400 hover:text-red-300 bg-red-900/20 hover:bg-red-900/40 border border-red-900/40 px-3 py-1.5 rounded transition-colors"
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-4xl mx-auto px-4 py-6">

        {/* 統計バー */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "全投稿", count: posts.length, color: "border-[#3a3a6a] text-[#8888cc]" },
            { label: "表示中", count: activeCount, color: "border-green-900/40 text-green-400" },
            { label: "削除済み", count: deletedCount, color: "border-red-900/40 text-red-400" },
          ].map(s => (
            <div
              key={s.label}
              className={`bg-[#1a1a2e] border ${s.color} rounded-lg px-4 py-3 text-center`}
            >
              <div className={`text-2xl font-bold ${s.color.split(" ")[1]}`}>{s.count}</div>
              <div className="text-[10px] text-[#5555aa] mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* フィルター＆検索 */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="flex gap-1">
            {(["all", "active", "deleted"] as const).map(f => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`text-[10px] px-3 py-1.5 rounded transition-colors ${
                  filter === f
                    ? "bg-[#3333aa] text-white"
                    : "bg-[#1a1a2e] text-[#7777aa] hover:text-white border border-[#3a3a6a]"
                }`}
              >
                {f === "all" ? "すべて" : f === "active" ? "表示中" : "削除済み"}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            placeholder="名前・本文で検索…"
            className="flex-1 bg-[#1a1a2e] border border-[#3a3a6a] rounded px-3 py-1.5 text-white text-xs outline-none focus:border-[#6666aa] placeholder:text-[#444] transition-colors"
          />
        </div>

        {/* 投稿リスト */}
        {loading ? (
          <div className="text-center py-16 text-[#5555aa]">
            <div className="text-3xl mb-3">📡</div>
            <p className="text-sm">読み込み中…</p>
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-16 text-[#5555aa]">
            <div className="text-3xl mb-3">🌙</div>
            <p className="text-sm">投稿がありません</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredPosts.map((post, i) => (
              <div
                key={post.id}
                className={`rounded-lg border transition-all ${
                  post.is_deleted
                    ? "bg-[#1a0a0a]/60 border-red-900/30 opacity-60"
                    : "bg-[#1a1a2e] border-[#2a2a4a] hover:border-[#3a3a6a]"
                }`}
              >
                {/* カードヘッダー */}
                <div className="flex items-center justify-between px-4 py-2 border-b border-[#2a2a4a]/60">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono text-[#5555aa] bg-[#0d0d1a] px-1.5 py-0.5 rounded">
                      #{String(i + 1).padStart(4, "0")}
                    </span>
                    <span className="text-xs font-bold text-[#aaaaff]">{post.author}</span>
                    {post.is_deleted && (
                      <span className="text-[9px] bg-red-900/40 text-red-400 border border-red-900/40 px-1.5 py-0.5 rounded">
                        削除済
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[9px] text-[#444466] font-mono">
                      ID: {post.id.slice(0, 8)}…
                    </span>
                    {post.ip_hash && (
                      <span className="text-[9px] text-[#444466] font-mono hidden sm:block">
                        IP: {post.ip_hash.slice(0, 8)}…
                      </span>
                    )}
                    <span className="text-[9px] text-[#555577] font-mono">
                      {toMatoDate(post.created_at)}
                    </span>
                  </div>
                </div>

                {/* 本文 + 操作ボタン */}
                <div className="px-4 py-3 flex items-start justify-between gap-3">
                  <p className={`text-sm whitespace-pre-wrap leading-relaxed break-words flex-1 ${
                    post.is_deleted ? "text-[#555566] line-through" : "text-[#ccccee]"
                  }`}>
                    {post.message}
                  </p>
                  <div className="flex gap-2 shrink-0">
                    {!post.is_deleted ? (
                      <button
                        onClick={() => handleDelete(post.id)}
                        disabled={deletingId === post.id}
                        className="text-[10px] bg-red-800 hover:bg-red-600 disabled:bg-red-900/40 text-white px-3 py-1.5 rounded transition-colors disabled:cursor-not-allowed font-bold flex items-center gap-1"
                      >
                        {deletingId === post.id ? (
                          <span>削除中…</span>
                        ) : (
                          <>🗑 削除</>
                        )}
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRestore(post.id)}
                        disabled={deletingId === post.id}
                        className="text-[10px] bg-[#1a3a1a] hover:bg-green-800 disabled:opacity-50 text-green-400 hover:text-white border border-green-900/40 px-3 py-1.5 rounded transition-colors disabled:cursor-not-allowed flex items-center gap-1"
                      >
                        {deletingId === post.id ? (
                          <span>処理中…</span>
                        ) : (
                          <>↩ 復元</>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* フッター */}
        <div className="mt-10 text-center border-t border-[#1a1a3a] pt-4">
          <p className="text-[10px] text-[#333355]">
            魔都市政府 内部管理システム v2.26 — AUTHORIZED PERSONNEL ONLY
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────
   エクスポート
───────────────────────────────────────── */
export default function AdminPanel() {
  const [loggedIn, setLoggedIn] = useState(false);

  return loggedIn ? (
    <AdminPanelContent onLogout={() => setLoggedIn(false)} />
  ) : (
    <LoginScreen onLogin={() => setLoggedIn(true)} />
  );
}
